import { GTAG_COOKIE_FLAGS, GTAG_SCRIPT_URL } from '../constants';
import { loadScript } from '../loadScript';
import type { GoogleAdsConfig, TrackingEvent, TrackingProvider } from '../types';

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

export interface GtagTargets {
  ga4MeasurementId: string | null;
  googleAds: GoogleAdsConfig | null;
}

function installGtag(): Gtag {
  if (window.gtag) return window.gtag;
  const dataLayer = (window.dataLayer ??= []);
  const gtag: Gtag = function gtag() {
    // gtag.js reads only Arguments objects off the dataLayer; a plain array is ignored.
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments);
  };
  window.gtag = gtag;
  return gtag;
}

function amount(event: TrackingEvent): Record<string, unknown> {
  return event.value !== null ? { value: event.value, currency: event.currency } : {};
}

function transaction(event: TrackingEvent): Record<string, unknown> {
  return event.orderId !== null ? { transaction_id: event.orderId } : {};
}

function ga4Params(event: TrackingEvent, measurementId: string): Record<string, unknown> {
  const item = {
    item_id: event.offerId,
    item_name: event.offerTitle,
    quantity: event.quantity,
    ...(event.value !== null ? { price: Math.round((event.value / event.quantity) * 100) / 100 } : {}),
  };
  return { send_to: measurementId, items: [item], ...amount(event), ...transaction(event) };
}

function adsLabel(event: TrackingEvent, ads: GoogleAdsConfig): string | null {
  if (event.name === 'purchase') return ads.purchaseLabel;
  if (event.name === 'begin_checkout') return ads.checkoutLabel;
  return null;
}

/** GA4 and Google Ads share one gtag.js; null when neither is configured. */
export function createGtagProvider({ ga4MeasurementId, googleAds }: GtagTargets): TrackingProvider | null {
  const scriptId = ga4MeasurementId ?? googleAds?.conversionId;
  if (!scriptId) return null;
  return {
    install() {
      const gtag = installGtag();
      loadScript(`${GTAG_SCRIPT_URL}?id=${encodeURIComponent(scriptId)}`);
      gtag('js', new Date());
      gtag('set', { cookie_flags: GTAG_COOKIE_FLAGS });
      // Each config reports its target's page view — the one page view per load.
      if (ga4MeasurementId) gtag('config', ga4MeasurementId);
      if (googleAds) gtag('config', googleAds.conversionId);
    },
    track(event) {
      const gtag = window.gtag;
      if (!gtag) return;
      if (ga4MeasurementId) gtag('event', event.name, ga4Params(event, ga4MeasurementId));
      const label = googleAds ? adsLabel(event, googleAds) : null;
      if (googleAds && label) {
        gtag('event', 'conversion', {
          send_to: `${googleAds.conversionId}/${label}`,
          ...amount(event),
          ...transaction(event),
        });
      }
    },
  };
}
